import { useState } from "react";
import { useRecoilState } from "recoil";
import { Flex } from "rebass/styled-components";
import {
  ContainerWrapper,
  ContentContainer,
  PinnedFooterRow,
  SaveButton,
} from "./styledComponents";
import {
  FullPageBackgroundContainer,
  Page,
  ResponsiveColumn,
  StaticColumn,
  StyledDivider,
} from "../../StyledComponents";
import { Header } from "../Header";
import { useTranslation } from "react-i18next";
import { SignaturePreview } from "./Components/SignaturePreview";
import { SignatureSelector } from "./Components/SignatureSelector";
import { LoadingSpinner } from "../Lottie";
import { SignatureSelectorViewModel } from "./Components/SignatureSelector/ViewModel";
import { SignatureSelectorState } from "../../State/loadoutSelector";
import { Button } from "../Button";
import { useWindowSize } from "../../../hooks";
import theme from "../../../theme/themeThree";
import { useShouldShowSubscriptionOverhaul } from "MVVM/Hooks/useSubscriptionOverhaul";
import { usePortal } from "MVVM/Hooks/usePortal";
import { UnifiedUpgradeModal } from "MVVM/Components/Modals/UnifiedUpgradeModal";

const { REACT_APP_S3_CLIP_BUCKET } = process.env;
export const SignatureSelectorModal = ({
  onSave,
  onCloseClick,
}: {
  onSave: () => void;
  onCloseClick: () => void;
}) => {
  const { t } = useTranslation();
  const MEDIA_PREFIX = `${REACT_APP_S3_CLIP_BUCKET}/static/ftue`;
  const { width = 0 } = useWindowSize();
  const standardToNumber = parseInt(theme.breaks.standard.replace("px", ""));

  return (
    <>
      <ContainerWrapper
        flexDirection={"column"}
        style={{ position: "relative" }}
        className="total-wrapper"
      >
        <ContentContainer>
          <FullPageBackgroundContainer
            backgroundImage={`${MEDIA_PREFIX}/9_cs.webp`}
          />
          <ResponsiveColumn>
            <Flex flexDirection={"column"}>
              <Header
                headerText={t("Set your Signature look")}
                subHeaderText={t(
                  "Want to brand your clips? Update your theme, style, and settings to personalize your clips",
                )}
              />
            </Flex>
            <StyledDivider />
            {width > standardToNumber && <SignaturePreview />}
          </ResponsiveColumn>
          <StaticColumn>
            <Page>
              {width <= standardToNumber && (
                <>
                  <SignaturePreview />
                </>
              )}
              <SignatureSelector />
              <Flex>
                <Save onSave={onSave} onCloseClick={onCloseClick} />
              </Flex>
            </Page>
          </StaticColumn>
        </ContentContainer>
      </ContainerWrapper>
    </>
  );
};

const Save = ({
  onSave,
  onCloseClick,
}: {
  onSave: () => void;
  onCloseClick: () => void;
}) => {
  const showSubscriptionOverhaul = useShouldShowSubscriptionOverhaul();

  const {
    portalOpen: subscriptionPaywallOpen,
    setPortalOpen: setSubscriptionPaywallOpen,
    Portal: SubscriptionPaywallPortal,
  } = usePortal(
    <UnifiedUpgradeModal
      featureName="Signature Styles"
      upgradeAction="Use Signature Styles"
      requiredTierKey="ESSENTIALS"
      analyticsSource="Signatures"
      onClose={() => setSubscriptionPaywallOpen(false)}
    />,
  );

  const { saveSignature } = SignatureSelectorViewModel({
    onSave,
    onPaywalled: () => {
      if (showSubscriptionOverhaul) {
        setSubscriptionPaywallOpen(true);
      } else {
        onCloseClick();
      }
    },
  });
  const [signatureSelectorState, setSignatureSelectorState] = useRecoilState(
    SignatureSelectorState,
  );

  const [loading, setLoading] = useState(false);

  return (
    <>
      <PinnedFooterRow>
        <Button
          size="small"
          flex={true}
          classification="roundedFilledAlt"
          onClick={onCloseClick}
        >
          Close
        </Button>

        {loading && <LoadingSpinner width={30} height={30} />}

        {!loading && (
          <SaveButton
            size="small"
            classification="roundedFilled"
            disabled={!signatureSelectorState.allowSave}
            onClick={async () => {
              setLoading(true);

              await saveSignature();
              setLoading(false);
            }}
          >
            Save & Close
          </SaveButton>
        )}
      </PinnedFooterRow>
      {subscriptionPaywallOpen && SubscriptionPaywallPortal}
    </>
  );
};
